import React, { useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { useFormV3 } from "../../../../context/FormV3Context/FormV3Context";
import { Button, Spin, Message } from "../../../BP2Components";
import { Icon_Download } from "../../../BP2Components/Icons/icons";
import { isFieldDisabled } from "./FormFIeldsV3Decorators";
import "../formsV3.less";
import "../../../BP2Components/Upload/Upload.less";
import { uploadFieldFile } from "../../../../services/restAPI/FormsV3";

interface FormV3UploadFileProps {
   field: any;
   formGUID: string;
   loading?: boolean;
   onValueChange: (payload: { field: any; value: any; formGUID: string }) => void;
   formId?: string;
}

type SimpleFileMeta = {
   id: string | number;
   url?: string;
   name: string;
};

const normalizeInitialFile = (rawValue): SimpleFileMeta | null => {
   if (!rawValue) return null;

   if (typeof rawValue === "string" || typeof rawValue === "number") {
      return { id: rawValue, name: String(rawValue) };
   }

   if (typeof rawValue === "object") {
      // form API wraps the file in { originalValue, decoratedValue }; upload API returns the file object directly
      if ("decoratedValue" in rawValue) {
         const decorated = rawValue.decoratedValue;
         return {
            id: rawValue.originalValue,
            url: typeof decorated === "string" ? decorated : decorated?.rawUrl,
            name:
               (typeof decorated === "object" ? (decorated?.name ?? decorated?.fileName) : null) ??
               "File",
         };
      }

      return {
         id: rawValue.id,
         url: rawValue.rawUrl,
         name: rawValue.name ?? rawValue.fileName ?? "File",
      };
   }

   return null;
};

const ALLOWED_PROTOCOLS = new Set(["http:", "https:"]);

const resolveUrl = (rawUrl: string): URL | null => {
   try {
      const resolved = /^https?:\/\//i.test(rawUrl)
         ? new URL(rawUrl)
         : new URL(rawUrl, window.location.origin);
      return ALLOWED_PROTOCOLS.has(resolved.protocol) ? resolved : null;
   } catch {
      return null;
   }
};

const openInNewTab = (url: string) => {
   const win = window.open(url, "_blank", "noopener,noreferrer");
   if (!win) {
      const a = document.createElement("a");
      a.href = url;
      a.target = "_blank";
      a.rel = "noopener noreferrer";
      document.body.appendChild(a);
      a.click();
      a.remove();
   }
};

const FormV3UploadField = ({
   field,
   formGUID,
   loading,
   onValueChange,
   formId,
}: FormV3UploadFileProps) => {
   const intl = useIntl();
   const { formV3Data, changeFieldValue, fieldsInfo } = useFormV3();
   const isDisabled = isFieldDisabled(field);
   const fieldInfo = fieldsInfo[formGUID]?.[field.id];

   const fileInputRef = useRef<HTMLInputElement | null>(null);

   const [fileMeta, setFileMeta] = useState<SimpleFileMeta | null>(() =>
      normalizeInitialFile(field?.value),
   );
   const [isUploading, setIsUploading] = useState(false);

   const handleValueChange = (nextValueForContext: any, meta: SimpleFileMeta | null) => {
      setFileMeta(meta);
      changeFieldValue(formGUID, field.id, nextValueForContext);
      onValueChange({ field, value: nextValueForContext, formGUID });
   };

   const ctxValue = formV3Data?.[formGUID]?.[field.id];
   useEffect(() => {
      const normalized = normalizeInitialFile(ctxValue);
      setFileMeta((prev) => {
         if (
            normalized?.id !== prev?.id ||
            normalized?.url !== prev?.url ||
            normalized?.name !== prev?.name
         ) {
            return normalized;
         }
         return prev;
      });
   }, [ctxValue]);

   const handleBrowseClick = () => {
      if (isDisabled || isUploading) return;
      fileInputRef.current?.click();
   };

   const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
      const selectedFile = e.target.files?.[0];
      if (!selectedFile) return;

      try {
         if (!formId) throw new Error("formId is required for file upload");
         const fieldName = field.modelField?.name;
         if (!fieldName) throw new Error(`Upload field ${field?.id} is missing modelField.name`);

         setIsUploading(true);
         Message.loading(intl.formatMessage({ id: "global.loading" }));

         const currentFieldId = String(field.id);
         const rawValues = formV3Data?.[formGUID] ?? {};
         const commandPayload: Record<string, any> = {};
         for (const [fieldId, val] of Object.entries(rawValues)) {
            if (fieldId === currentFieldId) continue;
            commandPayload[fieldId] =
               val && typeof val === "object" && !Array.isArray(val)
                  ? ((val as any).originalValue ?? (val as any).value ?? val)
                  : val;
         }

         const formData = new FormData();
         formData.append(
            "command",
            new Blob([JSON.stringify(commandPayload)], { type: "application/json" }),
         );
         formData.append(currentFieldId, selectedFile);

         const res = await uploadFieldFile(formData, formId);

         const rawFileValue = res.data?.data?.[fieldName];
         if (!rawFileValue)
            throw new Error(`Server response missing file data for field: ${fieldName}`);
         const meta = normalizeInitialFile(rawFileValue);
         handleValueChange(
            { originalValue: rawFileValue.id ?? rawFileValue, decoratedValue: rawFileValue },
            meta,
         );

         Message.destroy();
         Message.success(intl.formatMessage({ id: "global.done" }));
      } catch (error) {
         console.error(error);
         Message.destroy();
         const isTooLarge = (error as any)?.response?.status === 413;
         Message.error(
            intl.formatMessage({ id: isTooLarge ? "upload.fileTooLarge" : "global.error" }),
         );
      } finally {
         setIsUploading(false);
         if (fileInputRef.current) {
            fileInputRef.current.value = "";
         }
      }
   };

   const handleDelete = () => {
      if (isDisabled) return;
      handleValueChange(null, null);
   };

   const handleDownload = () => {
      if (!fileMeta?.url) return;
      const resolved = resolveUrl(fileMeta.url);
      if (!resolved) return;
      if (resolved.origin === window.location.origin) {
         const a = document.createElement("a");
         a.href = resolved.href;
         a.download = fileMeta.name || "file";
         document.body.appendChild(a);
         a.click();
         a.remove();
         return;
      }
      openInNewTab(resolved.href);
   };

   const openFile = (file: SimpleFileMeta) => {
      if (!file?.url) return;
      const resolved = resolveUrl(file.url);
      if (!resolved) return;
      openInNewTab(resolved.href);
   };

   const hasFile = !!fileMeta;
   return (
      <>
         <span className="v3-field-element v3-field-upload">
            <div className="bp2upload">
               <div className="bp2upload__container">
                  {hasFile && fileMeta?.url && (
                     <div className="bp2upload__preview__icon">
                        <img
                           className="bp2upload__container__image"
                           src={fileMeta.url}
                           alt={fileMeta.name}
                           onClick={() => openFile(fileMeta)}
                        />
                     </div>
                  )}
               </div>

               <div
                  className={
                     hasFile
                        ? "bp2upload__preview"
                        : "bp2upload__preview bp2upload__preview--no-border"
                  }
               >
                  <div className="bp2upload__preview__actions">
                     <div style={{ lineHeight: 0 }} onClick={handleBrowseClick}>
                        <Button disabled={isDisabled || isUploading} buttonType="primary">
                           {intl.formatMessage({
                              id: `${!(hasFile && fileMeta?.url) ? "upload.addImage" : "upload.changeImage"}`,
                           })}
                        </Button>
                     </div>

                     <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleFileChange}
                        style={{ display: "none" }}
                        disabled={isDisabled || isUploading}
                     />

                     {hasFile && (
                        <>
                           <Button disabled={isDisabled || isUploading} onClick={handleDownload}>
                              <Icon_Download fill="none" stroke="black" />
                           </Button>
                           <Button
                              disabled={isDisabled || isUploading}
                              buttonType="danger"
                              onClick={handleDelete}
                           >
                              <span className="icon icon-trash" />
                           </Button>
                        </>
                     )}
                  </div>
               </div>
            </div>

            <span className="p2 dev-info">{`${field.id}`}</span>
            {fieldInfo ? <div className="validation-error">{`${fieldInfo}`}</div> : null}
         </span>
         {(loading || isUploading) && <Spin />}
      </>
   );
};

export default FormV3UploadField;
